import type { CustomCellRendererProps } from "ag-grid-react";
import i18n from "i18next";
import moment from "moment";
import React from "react";
import { useDispatch } from "react-redux";
import Link from "redux-first-router-link";
import { Flex } from "@unifize/sarah";
import k from "src/i18n/keys";
import * as atypes from "src/constants/actionTypes";
import * as status from "src/constants/status";
import * as colors from "src/styles/constants/colors";
import Icon from "src/icons";
import { reportModalAttributes } from "src/actions/reports";
import type { Nullable } from "src/ts-types";
import type { Report } from "src/types";
import { ActionsContainer, NewBadge, ActionButton } from "./styles";

interface CellRendererParams extends CustomCellRendererProps {
  data: Report;
}

export const ReportNameCellRenderer = ({
  data,
}: CellRendererParams): Nullable<React.ReactElement> => {
  const dispatch = useDispatch();

  if (!data || !data.id) return null;

  const handleReport = (type: "update" | "delete") => {
    dispatch(
      reportModalAttributes({
        modalType: type,
        loading: false,
        title: data.title,
        id: data.id,
      }),
    );
  };

  const isNew =
    moment()
      .startOf("day")
      .diff(
        moment.unix(data.createdAt.seconds).startOf("day"),
        i18n.t(k.DAYS),
      ) === 0;

  return (
    <Flex
      style={{
        alignItems: "center",
        position: "relative",
        width: "100%",
        height: "100%",
        padding: "0 8px",
      }}
    >
      <Flex style={{ alignItems: "center", flex: 1, minWidth: 0 }}>
        <Link
          to={{
            type: atypes.SET_REPORTS_REQUEST,
            payload: {
              id: data.id,
            },
          }}
          style={{
            maxWidth: "50%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {data.title}
        </Link>
      </Flex>
      {isNew && <NewBadge status={status.NEW}>{i18n.t(k.NEW)}</NewBadge>}
      <ActionsContainer>
        <ActionButton onClick={() => handleReport("update")}>
          <Icon type="edit" fill={colors.active} color={colors.active} />
          <span>{i18n.t(k.EDIT_REPORT_TITLE)}</span>
        </ActionButton>
        <ActionButton onClick={() => handleReport("delete")}>
          <Icon type="delete" color={colors.danger} />
          <span>{i18n.t(k.DELETE)}</span>
        </ActionButton>
      </ActionsContainer>
    </Flex>
  );
};
